/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, Inject, Input, OnInit, signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import _ from 'lodash';

type Attachment =
  | { kind: 'image'; src: string }
  | { kind: 'embed'; html: SafeHtml }
  | { kind: 'link'; url: string };

// Embedly iframes arrive without a title, which screen readers need to name the frame.
function titleFrames(html: string, title?: string): string {
  return html.replace(/<iframe(?![^>]*\stitle=)/gi, `<iframe title="${_.escape(title || 'Embedded content')}"`);
}

// Angular replacement for the AngularJS `status-attachment`: renders a status's attached image, embed or link.
@Component({
  selector: 'ut-status-attachment',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  template: `
    @switch (attachment()?.kind) {
      @case ('image') {
        <a class="status-attachment__image" uiSref="singlestatus" [uiParams]="{ statusId: status.id }" aria-label="View status image">
          <img [src]="$any(attachment()).src" alt="" />
        </a>
      }
      @case ('embed') {
        <div class="status-attachment__embed" [innerHTML]="$any(attachment()).html"></div>
      }
      @case ('link') {
        <a class="status-attachment__link" [href]="$any(attachment()).url" target="_blank" rel="noopener noreferrer">{{ $any(attachment()).url }}</a>
      }
    }
  `,
  styles: [`
    :host {
      display: block;
    }

    .status-attachment__image {
      display: inline-block;
      overflow: hidden;
      border: 1px solid var(--border-secondary);
      border-radius: var(--radius-md);
    }

    .status-attachment__image img {
      display: block;
      max-width: 100%;
      max-height: var(--status-attachment-max-height, 32rem);
      object-fit: cover;
    }

    .status-attachment__embed {
      overflow: hidden;
      border-radius: var(--radius-md);
    }

    .status-attachment__embed ::ng-deep iframe {
      width: 100%;
      max-height: var(--status-attachment-max-height, none);
      aspect-ratio: 16 / 9;
      height: auto;
      border: 0;
    }

    .status-attachment__link {
      color: var(--text-brand-secondary);
      font-weight: 600;
      word-break: break-all;
    }
  `],
})
export class StatusAttachmentComponent implements OnInit {
  @Input({ required: true }) status: any;

  attachment = signal<Attachment | null>(null);

  constructor(
    @Inject('EmbedlyService') private EmbedlyService: any,
    @Inject('CookieAgreementService') private CookieAgreementService: any,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    const url: string = this.status.attached_url;
    if (url.startsWith('/') || /\.gif(\?|$)/i.test(url)) {
      this.attachment.set({ kind: 'image', src: url });
    } else if (!this.CookieAgreementService.hasAcceptedTargetingCookies()) {
      this.attachment.set({ kind: 'link', url });
    } else {
      this.EmbedlyService.getEmbed(url).then(({ data }) => {
        // Embed HTML comes from our own /api/v1/embeds endpoint (as in the AngularJS version).
        this.attachment.set(data.html
          ? { kind: 'embed', html: this.sanitizer.bypassSecurityTrustHtml(titleFrames(data.html, data.title)) }
          : { kind: 'image', src: data.url });
      }, () => this.attachment.set({ kind: 'link', url }));
    }
  }
}
